import type { ComponentType } from "react";
import { Suspense, useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import { lazily } from "react-lazily";
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  Route,
  useOutletContext,
} from "react-router";

import { AdminRoute } from "app/components/AdminRoute/AdminRoute";
import {
  RESET_PASSWORD_PATH,
  ResetPasswordPage,
} from "app/components/authentication/ResetPasswordPage";
import { CircularFallback } from "app/components/fallbacks";
import { DetailsSkeleton, TableSkeleton } from "app/components/skeletons";
import { paths } from "app/constants/paths";
import { useConfig } from "app/contexts";
import { useIsDatadogAvailable } from "app/hooks/feature-flags/useIsDatadogAvailable";
import { useRouteParams } from "app/hooks/routing/useRouteParams";
import { accountGroupDetailsRoute } from "app/views/admin/account-groups/details/accountGroupDetailsRoute";
import { accountsRoute } from "app/views/admin/accounts/accountsRoute";
import { accountDetailsRoute } from "app/views/admin/accounts/details/accountDetailsRoute";
import { bindingsRoute } from "app/views/admin/bindings/bindingsRoute";
import { bindingDetailsRoute } from "app/views/admin/bindings/details/bindingDetailsRoute";
import { policiesRoute } from "app/views/admin/policies/policiesRoute";
import { DashboardProviderGuard } from "app/views/dashboards/components/DashboardProviderGuard";
import { CostDashboardBarStateProvider } from "app/views/dashboards/contexts/CostDashboardBarContext";
import { NotFound } from "app/views/NotFound";
import { CreateNotificationRuleRoot } from "app/views/notifications/rules/create/CreateNotificationRuleRoot";
import { EditNotificationRuleRoot } from "app/views/notifications/rules/edit/EditNotificationRuleRoot";
import { OnboardingContext } from "app/views/onboarding/context/OnboardingContext";
import { policyDetailsRoute } from "app/views/overview/policies/details/policyDetailsRoute";
import { GroupsSettingsRoot } from "app/views/settings/access-control/GroupsSettingsRoot";
import { IdentityProvidersSettingsRoot } from "app/views/settings/access-control/IdentityProvidersSettingsRoot";
import { EditGroupRoot } from "app/views/settings/access-control/UpdateGroupForm/EditGroupRoot";
import { UsersSettingsRoot } from "app/views/settings/access-control/UsersSettingsRoot";
import { AccessModelSettingsRoot } from "app/views/settings/access-model/AccessModelSettingsRoot";
import { CostSettingsRoot } from "app/views/settings/cost/CostSettingsRoot";
import { CreateDpcRoot } from "app/views/settings/dynamic-policy-collections/create/CreateDpcRoot";
import { EditDpcRoot } from "app/views/settings/dynamic-policy-collections/edit/EditDpcRoot";

import { App } from "./App";
import { AuthedLayout, RootLayout } from "./RootLayout";

import type { RoutesOutletContext } from "./AppRoutesLayout";
import type { UpsertAWSAccountDiscoveryInput } from "app/mutations/__generated__/UpsertAWSAccountDiscoveryMutation.graphql";

// NOTE: lazy imports must not import from barrels

// onboarding
const { OnboardingWizardRoot } = lazily(
  () => import("app/views/onboarding/OnboardingWizardRoot"),
);

// overview
const { Activity } = lazily(() => import("app/views/overview"));
const { Cloud } = lazily(() => import("app/views/dashboards/cloud/Cloud"));
const { Jun0 } = lazily(() => import("app/views/jun0/Jun0"));

// Conditional Cloud component that redirects if disabled
function ConditionalCloudRoute() {
  const config = useConfig();
  const hiddenDashboardKeys = config?.hidden_dashboard_keys ?? [];
  const isCloudHidden = hiddenDashboardKeys.includes("cloud");

  if (isCloudHidden) {
    return <Navigate to={paths.activity} replace />;
  }

  return <Cloud />;
}

// Conditional redirect components.
function DefaultDashboardRedirect() {
  const { jun0Url } = useOutletContext<RoutesOutletContext>();
  const config = useConfig();
  const hiddenDashboardKeys = config?.hidden_dashboard_keys ?? [];
  const target = jun0Url
    ? paths.jun0
    : hiddenDashboardKeys.includes("cloud")
      ? paths.activity
      : paths.dashboardsCloud;
  return <Navigate to={target} replace />;
}

// Conditional Jun0 component that redirects if backend is absent
function ConditionalJun0Route() {
  const { jun0Url } = useOutletContext<RoutesOutletContext>();
  if (!jun0Url) {
    return <Navigate to={paths.overview} replace />;
  }
  return <Jun0 jun0Url={jun0Url} />;
}

// Redirect old user details route to new location
function UserDetailsRedirect() {
  const { key = "" } = useRouteParams(paths.legacyUserDetails);
  return <Navigate to={generatePath(paths.userDetails, { key })} replace />;
}

function PolicyUUIDRedirect() {
  const { policyUUID = "" } = useRouteParams(paths.policyDetails);
  return (
    <Navigate
      to={generatePath(paths.adminPolicyDetails, { policyUUID })}
      replace
    />
  );
}

function PolicyVersionRedirect() {
  const { policyUUID = "", versionNumber = "" } = useRouteParams(
    paths.policyVersionDetails,
  );
  return (
    <Navigate
      to={generatePath(paths.adminPolicyVersionDetails, {
        policyUUID,
        versionNumber,
      })}
      replace
    />
  );
}

function PolicyExecutionRedirect() {
  const { policyUuid = "", executionId = "" } = useRouteParams(
    paths.policyExecutionDetails,
  );
  return (
    <Navigate
      to={generatePath(paths.adminPolicyExecutionDetails, {
        policyUuid,
        executionId,
      })}
      replace
    />
  );
}
const { ExecutionDetailsRoot } = lazily(
  () => import("app/views/overview/policies/details/ExecutionDetailsRoot"),
);

// dashboards
const { CostDashboard } = lazily(
  () => import("app/views/dashboards/cost/CostDashboard"),
);
const { ComplianceDashboard } = lazily(
  () => import("app/views/dashboards/components"),
);
const { CISDashboard } = lazily(
  () => import("app/views/dashboards/cis/CISDashboard"),
);
const { SectionDetails } = lazily(
  () => import("app/views/dashboards/details/SectionDetails"),
);
const { OpportunityDetails } = lazily(
  () => import("app/views/dashboards/cost/details/OpportunityDetails"),
);

// cloudops
const { ResourcesRoot } = lazily(
  () => import("app/views/cloudops/ResourcesRoot"),
);
const { ResourceDetailsRoot } = lazily(
  () => import("app/views/cloudops/resources/details/ResourceDetailsRoot"),
);

// admin
const { BindingDetailsRoot } = lazily(
  () => import("app/views/admin/bindings/details/BindingDetailsRoot"),
);
const { CreateBindingRoot } = lazily(
  () => import("app/views/admin/bindings/create/CreateBindingRoot"),
);
const { EditBindingRoot } = lazily(
  () => import("app/views/admin/bindings/edit/EditBindingRoot"),
);
const { RunDetailsRoot } = lazily(
  () => import("app/views/admin/bindings/components/RunDetailsRoot"),
);
const { AccountDetailsRoot } = lazily(
  () => import("app/views/admin/accounts/details/AccountDetailsRoot"),
);
const { AddAccountRoot } = lazily(
  () => import("app/views/admin/accounts/add/AddAccountRoot"),
);
const { EditAccountRoot } = lazily(
  () => import("app/views/admin/accounts/edit/EditAccountRoot"),
);
const { AccountGroupDetailsRoot } = lazily(
  () =>
    import("app/views/admin/account-groups/details/AccountGroupDetailsRoot"),
);
const { AccountGroupCreateRoot } = lazily(
  () => import("app/views/admin/account-groups/create/AccountGroupCreateRoot"),
);
const { EditAccountGroupRoot } = lazily(
  () => import("app/views/admin/account-groups/edit/EditAccountGroupRoot"),
);
const { CreatePolicyCollectionRoot } = lazily(
  () =>
    import("app/views/admin/policy-collections/create/CreatePolicyCollectionRoot"),
);
const { PolicyCollectionDetails } = lazily(
  () =>
    import("app/views/admin/policy-collections/details/PolicyCollectionDetails"),
);
const { EditPolicyCollectionRoot } = lazily(
  () =>
    import("app/views/admin/policy-collections/edit/EditPolicyCollectionRoot"),
);

// notifications
const { IntegrationsRoot } = lazily(
  () => import("app/views/notifications/integrations/IntegrationsRoot"),
);
const { SlackRoot } = lazily(
  () => import("app/views/notifications/integrations/slack/SlackRoot"),
);
const { ConfigureSlackWebhooksRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/slack/webhooks/configure/ConfigureSlackWebhooksRoot"),
);
const { EmailRoot } = lazily(
  () => import("app/views/notifications/integrations/email/EmailRoot"),
);
const { ConfigureEmailRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/email/configure/ConfigureEmailRoot"),
);
const { JiraRoot } = lazily(
  () => import("app/views/notifications/integrations/jira/JiraRoot"),
);
const { CreateJiraInstanceRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/jira/create/CreateJiraInstanceRoot"),
);
const { EditJiraInstanceRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/jira/edit/EditJiraInstanceRoot"),
);
const { ServiceNowRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/service-now/ServiceNowRoot"),
);
const { ConfigureServiceNowRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/service-now/configure/ConfigureServiceNowRoot"),
);
const { DatadogRoot } = lazily(
  () => import("app/views/notifications/integrations/datadog/DatadogRoot"),
);
const { ConfigureDatadogRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/datadog/configure/ConfigureDatadogRoot"),
);
const { TeamsRoot } = lazily(
  () => import("app/views/notifications/integrations/teams/TeamsRoot"),
);
const { SetupTeamsRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/teams/setup/SetupTeamsRoot"),
);
const { TeamsChannelMappingRoot } = lazily(
  () =>
    import("app/views/notifications/integrations/teams/components/TeamsChannelMapping/TeamsChannelMappingRoot"),
);
const { PersonalApiKeysCreateRoot } = lazily(
  () =>
    import("app/views/settings/personal-api-keys/create/PersonalApiKeysCreateRoot"),
);
const { PersonalApiKeysEditRoot } = lazily(
  () =>
    import("app/views/settings/personal-api-keys/edit/PersonalApiKeysEditRoot"),
);
const { NotificationsOverviewRoot } = lazily(
  () => import("app/views/notifications/overview"),
);
const { SettingsRoot } = lazily(
  () => import("app/views/notifications/settings/SettingsRoot"),
);
const { ConfigureNotificationSettingsRoot } = lazily(
  () =>
    import("app/views/notifications/settings/configure/ConfigureNotificationSettingsRoot"),
);
const { AddTemplateRoot } = lazily(
  () => import("app/views/notifications/settings/templates/AddTemplateRoot"),
);
const { EditTemplateRoot } = lazily(
  () => import("app/views/notifications/settings/templates/EditTemplateRoot"),
);

// settings
const { RepositoryDetailsRoot } = lazily(
  () => import("app/views/admin/repositories/details/RepositoryDetailsRoot"),
);
const { LegacyPoliciesRoot } = lazily(
  () => import("app/views/admin/repositories/details/LegacyPoliciesRoot"),
);
const { UserDetailsRoot } = lazily(
  () => import("app/views/settings/users/user-list/details/UserDetailsRoot"),
);
const { UserEditRoot } = lazily(
  () => import("app/views/settings/users/user-list/details/UserEditRoot"),
);
const { ProfileSettingsRoot } = lazily(
  () => import("app/views/settings/profile/ProfileSettingsRoot"),
);
const { AddIdentityProviderRoot } = lazily(
  () =>
    import("app/views/settings/access-control/AddIdentityProvider/AddIdentityProviderRoot"),
);
const { AddGroupRoot } = lazily(
  () =>
    import("app/views/settings/access-control/UpdateGroupForm/AddGroupRoot"),
);
const { EditIdentityProviderRoot } = lazily(
  () =>
    import("app/views/settings/access-control/EditIdentityProvider/EditIdentityProviderRoot"),
);
const { GroupDetailsRoot } = lazily(
  () =>
    import("app/views/settings/access-control/GroupDetails/GroupDetailsRoot"),
);
const { SamlProviderDetailsRoot } = lazily(
  () =>
    import("app/views/settings/access-control/IdentityProviderDetails/SamlProviderDetailsRoot"),
);
const { CloudOrganizationDetailsRoot } = lazily(
  () =>
    import("app/views/settings/access-control/IdentityProviderDetails/CloudOrganizationDetailsRoot"),
);
const { EditCloudOrganizationRoot } = lazily(
  () =>
    import("app/views/settings/access-control/EditCloudOrganization/EditCloudOrganizationRoot"),
);

const { EditRepositoryConfigRoot } = lazily(
  () => import("app/views/admin/repositories/edit/EditRepositoryConfigRoot"),
);

const { AddRepositoryConfigRoot } = lazily(
  () => import("app/views/admin/repositories/add/AddRepositoryConfigRoot"),
);

const { NotificationRulesRoot } = lazily(
  () => import("app/views/notifications/rules/NotificationRulesRoot"),
);
const { NotificationRulesDetailsRoot } = lazily(
  () =>
    import("app/views/notifications/rules/details/NotificationRulesDetailsRoot"),
);
const { NotificationGroupDetailsRoot } = lazily(
  () =>
    import("app/views/notifications/rules/group-details/NotificationGroupDetailsRoot"),
);

const { CloudsRoot } = lazily(
  () => import("app/views/settings/clouds/CloudsRoot"),
);
const { GcpDetailsRoot } = lazily(
  () => import("app/views/settings/clouds/gcp/details/GcpDetailsRoot"),
);
const { GcpDeploymentRoot } = lazily(
  () => import("app/views/settings/clouds/gcp/deployment/GcpDeploymentRoot"),
);

// api
const { Sandbox } = lazily(() => import("app/views/sandbox/Sandbox"));

interface DashboardContentProps {
  CostComponent: ComponentType;
  ComplianceComponent: ComponentType;
}
function DashboardContent({
  CostComponent,
  ComplianceComponent,
}: DashboardContentProps) {
  const { key, sectionKey } = useRouteParams(
    paths.dashboard,
    paths.dashboardSectionDetails,
  );

  if (key?.startsWith("cis-") && !sectionKey) {
    return <Navigate to={`${paths.dashboardsCis}?version=${key}`} replace />;
  }

  if (key?.includes("cost")) {
    return (
      <DashboardProviderGuard dashboardKey={key}>
        <CostDashboardBarStateProvider>
          <CostComponent />
        </CostDashboardBarStateProvider>
      </DashboardProviderGuard>
    );
  }

  return <ComplianceComponent />;
}

function AdminLayout() {
  return (
    <AdminRoute>
      <Outlet />
    </AdminRoute>
  );
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// Holds the cross-step onboarding state that used to live at the routes
// level; OnboardingContext is only consumed by the wizard steps.
function OnboardingRoot() {
  const [orgDetails, setOrgDetails] =
    useState<UpsertAWSAccountDiscoveryInput | null>(null);

  const orgData = useMemo(() => ({ orgDetails, setOrgDetails }), [orgDetails]);

  return (
    <OnboardingContext.Provider value={orgData}>
      <OnboardingWizardRoot />
    </OnboardingContext.Provider>
  );
}

// Feature-flagged routes stay in the static route tree; the guards render
// NotFound when the flag is off, matching the pre-data-router behaviour where
// the routes simply didn't exist and fell through to the catch-all.
function AccessModelRoutes() {
  const config = useConfig();
  return config?.dev_flags.includes("admin-settings") ? (
    <Outlet />
  ) : (
    <NotFound />
  );
}

function DatadogRoutes() {
  const isDatadogAvailable = useIsDatadogAvailable();
  return isDatadogAvailable ? <Outlet /> : <NotFound />;
}

// Only private instances can change the email configuration, so the configure
// route is gated on the same condition that shows the entry points on the
// email integration page.
function EmailConfigureRoutes() {
  const config = useConfig();
  return config?.private_instance ? <Outlet /> : <NotFound />;
}

export function createAppRouter() {
  return sentryCreateBrowserRouter(
    createRoutesFromElements(
      // The router renders nothing while a cold load's route loader runs —
      // which can be a whole session check (see canPreloadRouteQueries) — so
      // hydration needs a fallback of its own or a hard refresh of a preloaded
      // route is a blank page until then.
      <Route HydrateFallback={CircularFallback} element={<RootLayout />}>
        {/* Password-reset emails link here; the page must render without
            authentication, so it sits outside the authenticated layout. */}
        <Route element={<ResetPasswordPage />} path={RESET_PASSWORD_PATH} />
        <Route element={<AuthedLayout />}>
          <Route element={<App />}>
            {/* /onboarding */}
            <Route path="onboarding">
              <Route element={<OnboardingRoot />} index />
            </Route>

            {/* /overview */}
            <Route element={<Navigate to={paths.overview} />} index />
            <Route path="setup">
              <Route element={<Navigate to={paths.overview} />} index />
            </Route>
            <Route path="overview">
              <Route element={<DefaultDashboardRedirect />} index />
            </Route>

            {/* /activity */}
            <Route path="activity">
              <Route element={<Activity />} index />
            </Route>

            {/* /jun0 */}
            <Route path="jun0">
              <Route element={<ConditionalJun0Route />} index />
            </Route>

            {/* /dashboards */}
            <Route path="dashboards">
              <Route element={<DefaultDashboardRedirect />} index />
              <Route path="cloud">
                <Route element={<ConditionalCloudRoute />} index />
              </Route>
              <Route path="cis">
                <Route element={<CISDashboard />} index />
              </Route>
              <Route
                element={
                  <DashboardContent
                    ComplianceComponent={ComplianceDashboard}
                    CostComponent={CostDashboard}
                  />
                }
                path=":key"
              />
              <Route
                element={
                  <DashboardContent
                    ComplianceComponent={SectionDetails}
                    CostComponent={OpportunityDetails}
                  />
                }
                path=":key/details/:sectionKey"
              />
            </Route>

            {/* read-only (/bindings, /accounts, /account-groups, /policy-collections) */}
            <Route path="bindings">
              <Route
                element={<BindingDetailsRoot readOnly />}
                path=":bindingUUID"
              />
            </Route>
            <Route path="accounts">
              <Route
                element={<AccountDetailsRoot readOnly />}
                path=":accountKey"
              />
            </Route>
            <Route path="account-groups">
              <Route
                element={<AccountGroupDetailsRoot readOnly />}
                path=":accountGroupUUID"
              />
            </Route>
            <Route path="policy-collections">
              <Route
                element={<PolicyCollectionDetails readOnly />}
                path=":collectionId"
              />
            </Route>

            {/* /policies — redirects to admin */}
            <Route path="policies">
              <Route
                element={<Navigate to={paths.adminPolicies} replace />}
                index
              />
              <Route path=":policyUUID">
                <Route element={<PolicyUUIDRedirect />} index />
                <Route
                  element={<PolicyVersionRedirect />}
                  path="version/:versionNumber"
                />
              </Route>
              <Route
                element={<PolicyExecutionRedirect />}
                path=":policyUuid/execution/:executionId"
              />
            </Route>

            {/* /cloudops */}
            <Route path="cloudops">
              <Route
                element={<Navigate to={paths.resources} replace />}
                index
              />
              {/* legacy /cloudops/resource-matches → Matches tab */}
              <Route
                element={
                  <Navigate to={`${paths.resources}?tab=matches`} replace />
                }
                path="resource-matches"
              />
              <Route path="resources">
                <Route element={<ResourcesRoot />} index />
                <Route element={<ResourceDetailsRoot />} path=":resourceKey" />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <ExecutionDetailsRoot />
                    </Suspense>
                  }
                  path=":resourceKey/:executionId"
                />
              </Route>
            </Route>

            {/* /admin */}
            <Route path="admin">
              <Route element={<Navigate to="bindings" />} index />
              {/* /admin/accounts — a preloading route: see policies below. */}
              <Route path="accounts">
                <Route {...accountsRoute} index />
                <Route
                  element={
                    <AdminRoute>
                      <Suspense fallback={<DetailsSkeleton searchBar />}>
                        <AddAccountRoot />
                      </Suspense>
                    </AdminRoute>
                  }
                  path="add"
                />
                <Route
                  {...accountDetailsRoute}
                  path="details/:accountProvider/:accountKey"
                />
                <Route
                  element={
                    <AdminRoute>
                      <Suspense fallback={<DetailsSkeleton searchBar />}>
                        <EditAccountRoot />
                      </Suspense>
                    </AdminRoute>
                  }
                  path="details/:accountProvider/:accountKey/edit"
                />
              </Route>

              {/* /admin/policy-collections */}
              <Route path="policy-collections">
                <Route
                  element={
                    <Navigate
                      to={`${paths.adminPolicies}?tab=policy-collections`}
                      replace
                    />
                  }
                  index
                />
                <Route element={<CreatePolicyCollectionRoot />} path="create" />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <PolicyCollectionDetails />
                    </Suspense>
                  }
                  path=":collectionId"
                />
                <Route
                  element={<EditPolicyCollectionRoot />}
                  path=":collectionId/edit"
                />

                <Route path="dynamic">
                  <Route element={<CreateDpcRoot />} path="create" />
                  <Route element={<EditDpcRoot />} path="edit" />
                </Route>
              </Route>

              {/* /admin/account-groups */}
              <Route path="account-groups">
                <Route
                  element={
                    <Navigate
                      to={`${paths.adminAccounts}?tab=account-groups`}
                      replace
                    />
                  }
                  index
                />
                <Route element={<AccountGroupCreateRoot />} path="create" />
                <Route {...accountGroupDetailsRoute} path=":accountGroupUUID" />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <EditAccountGroupRoot />
                    </Suspense>
                  }
                  path=":accountGroupUUID/edit"
                />
              </Route>

              {/* /admin/bindings — a preloading route: see policies below. */}
              <Route path="bindings">
                <Route {...bindingsRoute} index />
                <Route
                  element={
                    // TODO: This route should 404 if user does not have _any_ deployable account groups
                    // https://stacklet.atlassian.net/browse/ENG-5231
                    <Suspense
                      fallback={<TableSkeleton size="large" topMargin={16} />}
                    >
                      <CreateBindingRoot />
                    </Suspense>
                  }
                  path="create"
                />

                {/* /admin/bindings/:uuid */}
                <Route path=":bindingUUID">
                  <Route {...bindingDetailsRoute} index />
                  <Route
                    element={
                      // TODO: This route should 404 if user does not have _any_ deployable account groups
                      // https://stacklet.atlassian.net/browse/ENG-5231
                      <EditBindingRoot />
                    }
                    path="edit"
                  />

                  {/* /admin/bindings/:uuid/run */}
                  <Route path="run">
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <RunDetailsRoot />
                        </Suspense>
                      }
                      path=":runID"
                    />
                  </Route>
                </Route>
              </Route>

              {/* /admin/policies — preloading routes bring their element,
                  loader and revalidation policy from a module colocated with
                  the view (see PreloadingRouteProps). */}
              <Route path="policies">
                <Route {...policiesRoute} index />
                <Route path=":policyUUID">
                  <Route {...policyDetailsRoute} index />
                  <Route
                    {...policyDetailsRoute}
                    path="version/:versionNumber"
                  />
                </Route>
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <ExecutionDetailsRoot />
                    </Suspense>
                  }
                  path=":policyUuid/execution/:executionId"
                />
              </Route>

              {/* /admin/repositories */}
              <Route path="repositories">
                <Route
                  element={
                    <Navigate
                      to={`${paths.adminPolicies}?tab=repositories`}
                      replace
                    />
                  }
                  index
                />
                <Route element={<AddRepositoryConfigRoot />} path="add" />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <RepositoryDetailsRoot />
                    </Suspense>
                  }
                  path=":repositoryUUID"
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <EditRepositoryConfigRoot />
                    </Suspense>
                  }
                  path=":repositoryUUID/edit"
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <LegacyPoliciesRoot />
                    </Suspense>
                  }
                  path=":repositoryUUID/legacy-policies"
                />
              </Route>
            </Route>

            {/* /notifications */}
            <Route element={<AdminLayout />} path="notifications">
              <Route element={<Navigate to="overview" replace />} index />
              <Route element={<NotificationsOverviewRoot />} path="overview" />

              {/* /notifications/settings */}
              <Route path="settings">
                <Route element={<SettingsRoot />} index />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <ConfigureNotificationSettingsRoot />
                    </Suspense>
                  }
                  path="configure"
                />

                {/* /notifications/settings/templates */}
                <Route path="templates">
                  <Route element={<AddTemplateRoot />} path="add" />
                  <Route element={<EditTemplateRoot />} path=":name" />
                </Route>
              </Route>

              <Route path="rules">
                <Route element={<NotificationRulesRoot />} index />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <CreateNotificationRuleRoot />
                    </Suspense>
                  }
                  path="create"
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <EditNotificationRuleRoot />
                    </Suspense>
                  }
                  path=":ruleUUID/edit"
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <NotificationGroupDetailsRoot />
                    </Suspense>
                  }
                  path=":ruleUUID/issues/:groupId"
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <NotificationRulesDetailsRoot />
                    </Suspense>
                  }
                  path=":ruleUUID"
                />
              </Route>
            </Route>

            {/* /settings */}
            <Route path="settings">
              <Route element={<Navigate to="profile" replace />} index />

              <Route path="profile">
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <ProfileSettingsRoot />
                    </Suspense>
                  }
                  index
                />
                <Route
                  element={
                    <Suspense fallback={<DetailsSkeleton />}>
                      <UserEditRoot />
                    </Suspense>
                  }
                  path="edit"
                />
              </Route>

              <Route element={<AdminLayout />}>
                {/* /settings/cost */}
                <Route path="cost">
                  <Route element={<CostSettingsRoot />} index />
                </Route>

                {/* /settings/integrations */}
                <Route path="integrations">
                  <Route element={<IntegrationsRoot />} index />
                  <Route path="slack">
                    <Route element={<SlackRoot />} index />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <ConfigureSlackWebhooksRoot />
                        </Suspense>
                      }
                      path="webhooks/configure"
                    />
                  </Route>
                  <Route path="email">
                    <Route element={<EmailRoot />} index />
                    <Route element={<EmailConfigureRoutes />}>
                      <Route
                        element={
                          <Suspense fallback={<DetailsSkeleton />}>
                            <ConfigureEmailRoot />
                          </Suspense>
                        }
                        path="configure"
                      />
                    </Route>
                  </Route>
                  <Route path="jira">
                    <Route element={<JiraRoot />} index />
                    <Route element={<CreateJiraInstanceRoot />} path="create" />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <EditJiraInstanceRoot />
                        </Suspense>
                      }
                      path=":uuid/edit"
                    />
                  </Route>
                  <Route path="service-now">
                    <Route element={<ServiceNowRoot />} index />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <ConfigureServiceNowRoot />
                        </Suspense>
                      }
                      path="configure"
                    />
                  </Route>
                  <Route element={<DatadogRoutes />}>
                    <Route path="datadog">
                      <Route element={<DatadogRoot />} index />
                      <Route
                        element={<ConfigureDatadogRoot />}
                        path="configure"
                      />
                    </Route>
                  </Route>
                  <Route path="teams">
                    <Route element={<TeamsRoot />} index />
                    <Route element={<SetupTeamsRoot />} path="setup" />
                    <Route
                      element={<TeamsChannelMappingRoot />}
                      path="channel-mapping"
                    />
                  </Route>
                </Route>

                {/* /settings/personal-api-keys */}
                <Route path="personal-api-keys">
                  <Route
                    element={<PersonalApiKeysCreateRoot />}
                    path="create"
                  />
                  <Route
                    element={<PersonalApiKeysEditRoot />}
                    path="edit/:identity"
                  />
                </Route>

                {/* /settings/access-control */}
                <Route path="access-control">
                  <Route element={<Navigate to="users" replace />} index />
                  <Route path="identity-providers">
                    <Route element={<IdentityProvidersSettingsRoot />} index />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <AddIdentityProviderRoot />
                        </Suspense>
                      }
                      path="add"
                    />
                    {/*
                The two kinds of provider are addressed differently — a SAML
                provider by name, a cloud organization by provider and key — so
                each gets its own prefix rather than sharing an ambiguous one.
              */}
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <SamlProviderDetailsRoot />
                        </Suspense>
                      }
                      path="saml/:providerName"
                    />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <EditIdentityProviderRoot />
                        </Suspense>
                      }
                      path="saml/:providerName/edit"
                    />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <CloudOrganizationDetailsRoot />
                        </Suspense>
                      }
                      path="organization/:provider/:key"
                    />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <EditCloudOrganizationRoot />
                        </Suspense>
                      }
                      path="organization/:provider/:key/edit"
                    />
                  </Route>
                  <Route path="users">
                    <Route element={<UsersSettingsRoot />} index />
                    <Route path=":key">
                      <Route
                        element={
                          <Suspense fallback={<DetailsSkeleton />}>
                            <UserDetailsRoot />
                          </Suspense>
                        }
                        index
                      />
                      <Route
                        element={
                          <Suspense fallback={<DetailsSkeleton />}>
                            <UserEditRoot />
                          </Suspense>
                        }
                        path="edit"
                      />
                    </Route>
                  </Route>
                  <Route path="groups">
                    <Route element={<GroupsSettingsRoot />} index />
                    <Route
                      element={
                        <Suspense fallback={<DetailsSkeleton />}>
                          <AddGroupRoot />
                        </Suspense>
                      }
                      path="add"
                    />
                    <Route path=":groupUuid">
                      <Route
                        element={
                          <Suspense fallback={<DetailsSkeleton />}>
                            <GroupDetailsRoot />
                          </Suspense>
                        }
                        index
                      />
                      <Route element={<EditGroupRoot />} path="edit" />
                    </Route>
                  </Route>
                </Route>

                {/* Redirect old user details route to new location */}
                <Route element={<UserDetailsRedirect />} path="users/:key" />

                <Route element={<AccessModelRoutes />}>
                  <Route path="access-model">
                    <Route element={<AccessModelSettingsRoot />} index />
                  </Route>
                </Route>

                {/* /settings/clouds */}
                <Route path="clouds">
                  <Route element={<CloudsRoot />} index />
                  <Route path="gcp">
                    <Route element={<GcpDetailsRoot />} path="details" />
                    <Route element={<GcpDeploymentRoot />} path="deployment" />
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* /api */}
            <Route element={<Sandbox />} path="api" />

            <Route element={<NotFound />} path="*" />
          </Route>
        </Route>
      </Route>,
    ),
  );
}
